import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  TEMPLATE_ACTIVE_STATUS,
  TEMPLATE_PAGE_SIZE_OPTIONS,
  TEMPLATE_PAGINATION_MODEL,
} from '@common/constants';

import PreviewIcon from '@mui/icons-material/Preview';
import Link from '@mui/material/Link';
import { DataGrid, GridFooter, GridFooterContainer } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import DrawIcon from '@mui/icons-material/Draw';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { getGridSingleSelectOperators } from '@mui/x-data-grid';

import {
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid';

import DetailsHeader from './DetailsHeader';
import { selectBackgroundColor } from '@common/providers/ThemeProvider/selectors';
import { Button } from '@mui/material';

const CustomToolbar = ({
  checkboxSelection = [],
  onExport = () => {},
  openUploadDialog = () => {},
  onDeleteTemplatesClick = () => {},
  onArchive = () => {},
  selectionArchiveValue,
}) => {
  const { t } = useTranslation();

  const exportButtonRef = useRef(null);
  const importButtonRef = useRef(null);

  const noSelection = !checkboxSelection || checkboxSelection?.length === 0;

  const getArchiveTooltipBySelection = (selectionArchiveValue) => {
    if (selectionArchiveValue === TEMPLATE_ACTIVE_STATUS.ACTIVE) {
      return t('setInactive');
    }

    if (selectionArchiveValue === TEMPLATE_ACTIVE_STATUS.ARCHIVED) {
      return t('setActive');
    }

    return t('disabledAsActiveStatusMixed');
  };

  const isArchivedSelected =
    selectionArchiveValue === TEMPLATE_ACTIVE_STATUS.ARCHIVED;

  return (
    <Toolbar
      sx={{
        color: (theme) => theme.palette.primary.main,
        justifyContent: 'flex-start',
      }}
    >
      <GridToolbarColumnsButton
        sx={{
          '& button': {
            p: '2px 7px',
          },
        }}
      />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <Tooltip title={t('export')}>
        <span>
          <Button
            id='export-button'
            ref={exportButtonRef}
            onClick={onExport}
            disabled={noSelection}
            startIcon={<FileDownloadIcon />}
          >
            {t('export')}
          </Button>
        </span>
      </Tooltip>

      <Tooltip title={t('import')}>
        <span>
          <Button
            id='import-button'
            ref={importButtonRef}
            onClick={openUploadDialog}
            startIcon={<FileUploadIcon />}
          >
            {t('import')}
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={getArchiveTooltipBySelection(selectionArchiveValue)}>
        <span>
          <Button
            id='set-inactive'
            onClick={() => onArchive(isArchivedSelected)}
            disabled={noSelection || !selectionArchiveValue}
            startIcon={<PowerSettingsNewIcon />}
            sx={{
              color: (theme) =>
                isArchivedSelected
                  ? theme?.palette?.action?.primary
                  : theme?.palette?.action.secondary,
            }}
          >
            {isArchivedSelected ? t('activate') : t('archive')}
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={t('delete')}>
        <span>
          <Button
            id='delete-button'
            onClick={onDeleteTemplatesClick}
            disabled={noSelection}
            startIcon={<DeleteForeverIcon />}
            color='error'
          >
            {t('delete')}
          </Button>
        </span>
      </Tooltip>
    </Toolbar>
  );
};

const CustomFooter = ({ exportCount }) => {
  const { t } = useTranslation();

  const message =
    exportCount > 0 ? t('export_selected', { count: exportCount }) : '';

  return (
    <GridFooterContainer>
      <div style={{ marginLeft: 16 }}>{message}</div>
      <GridFooter />
    </GridFooterContainer>
  );
};

const TemplateList = ({
  data,
  totalCount,
  loading,
  paginationModel,
  setPaginationModel,
  handleRowClick,
  selectedTemplate,
  goTo,
  onRightDrawerOpen = () => {},
  fetchEmailTemplatesPreview = () => {},
  onSortChange = () => {},
  onFilterChange = () => {},
  checkboxSelection = [],
  exportTemplates = () => {},
  handleExportCheckboxChange = () => {},
  openUploadDialog = () => {},
  onDeleteTemplatesClick = () => {},
  onArchive = () => {},
  selectionArchiveValue,
}) => {
  const { t } = useTranslation();

  const renderCheckboxCell = (params) => {
    return (
      <Checkbox
        size='small'
        checked={checkboxSelection.includes(params?.id || false)}
        onChange={(event, checked) => {
          const checkedId = params?.id;

          event.preventDefault();
          event.stopPropagation();

          handleExportCheckboxChange(checkedId, checked);
        }}
      />
    );
  };

  const columns = [
    {
      field: 'select',
      headerName: '',
      width: 50,
      sortable: false,
      editable: false,
      filterable: false,
      hideable: false,
      disableReorder: true,
      align: 'left',
      renderCell: renderCheckboxCell,
    },
    {
      field: 'id',
      headerName: t('id'),
      width: 250,
    },
    {
      field: 'name',
      headerName: t('name'),
      width: 250,
    },
    {
      field: 'dataTypeName',
      headerName: t('template_type'),
      width: 200,
    },
    // {
    //   field: 'templateType',
    //   headerName: t('template_type'),
    //   width: 200,
    //   renderCell: (params) => {
    //     return <div>{t(params.value)}</div>;
    //   },
    // },
    {
      field: 'isActive',
      headerName: `${t('active')}/${t('archived')}`,
      width: 150,
      filterable: true,
      type: 'singleSelect',
      filterOperators: getGridSingleSelectOperators()
        .filter((op) => op.value === 'is')
        .map((op) => ({
          ...op,
          label: t('is'),
        })),
      valueOptions: [
        { value: true, label: t('active') },
        { value: false, label: t('archived') },
      ],
      valueFormatter: (value) => {
        return value ? t('active') : t('archived');
      },
    },
    {
      field: 'actions',
      headerName: t('actions'),
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          variant='contained'
          color='primary'
          size='small'
          onClick={(event) => {
            event.stopPropagation();
            goTo(params?.id);
          }}
        >
          <DrawIcon />
        </IconButton>
      ),
    },
    {
      field: 'preview',
      headerName: t('preview'),
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Link
          component='div'
          variant='body2'
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            cursor: 'pointer',
          }}
          onClick={(e) => {
            e.stopPropagation();
            fetchEmailTemplatesPreview({ templateId: params?.row?.id });
          }}
        >
          <PreviewIcon />
          <Typography component={'span'}>{t('preview_button')}</Typography>
        </Link>
      ),
    },
  ];

  return (
    <Box>
      <DetailsHeader
        right={
          <IconButton
            onClick={onRightDrawerOpen}
            disabled={!selectedTemplate}
            sx={{
              width: '60px',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              paddingRight: 1,
              marginRight: 1,
              color: 'primary.main',
            }}
          >
            <MenuOpenIcon color={selectedTemplate ? 'primary' : 'secondary'} />
          </IconButton>
        }
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 105px)',
          p: 1,
          backgroundColor: (theme) => theme.palette.background.color,
        }}
      >
        <Typography variant='h6' component='h6' color='text.secondary'>
          {t('templates_menu_item')}
        </Typography>
        <DataGrid
          rows={data}
          columns={columns}
          rowCount={totalCount}
          loading={loading}
          pageSizeOptions={TEMPLATE_PAGE_SIZE_OPTIONS}
          pagination
          sortingMode='server'
          filterMode='server'
          paginationMode='server'
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sx={{
            height: '100%',
            overflowX: 'auto',
            width: 'calc(100vw - 220px)',
          }}
          slots={{
            toolbar: () => (
              <CustomToolbar
                checkboxSelection={checkboxSelection}
                onExport={exportTemplates}
                openUploadDialog={openUploadDialog}
                onDeleteTemplatesClick={onDeleteTemplatesClick}
                onArchive={onArchive}
                selectionArchiveValue={selectionArchiveValue}
              />
            ),
            footer: () => (
              <CustomFooter exportCount={checkboxSelection?.length} />
            ),
          }}
          onRowClick={handleRowClick}
          showToolbar
          onSortModelChange={onSortChange}
          onFilterModelChange={onFilterChange}
        />
      </Box>
    </Box>
  );
};

TemplateList.defaultProps = {
  handleRowClick: () => {},
  placeholder: '',
  checkboxes: null,
  showInactive: undefined,
  disabled: false,
  data: null,
  selectedTemplate: null,
  goTo: null,
  totalCount: 0,
  loading: false,
  paginationModel: TEMPLATE_PAGINATION_MODEL,
  setPaginationModel: () => {},
  checkboxSelection: [],
  exportTemplates: () => {},
  handleExportCheckboxChange: () => {},
};

TemplateList.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  disabled: PropTypes.bool,
  selectedTemplate: PropTypes.object,
  checkboxes: PropTypes.shape({
    onCheck: PropTypes.func,
    checkIfSelected: PropTypes.func,
  }),
  showInactive: PropTypes.bool,
  handleRowClick: PropTypes.func,
  placeholder: PropTypes.string,
  goTo: PropTypes.func,
  totalCount: PropTypes.number,
  loading: PropTypes.bool,
  paginationModel: PropTypes.object,
  setPaginationModel: PropTypes.func,
  checkboxSelection: PropTypes.array,
  exportTemplates: PropTypes.func,
  handleExportCheckboxChange: PropTypes.func,
};

export default TemplateList;
